import { questionPropertyMapper } from '@exam-management/common/utils';
import {
  AddQuestionQConfigStructureLocationType,
  type GetQConfigChapterSingleQuestionPropertiesCountQuery,
  useGetQConfigChapterSingleQuestionPropertiesCountQuery,
} from '@exam-management/graphql';
import { useMemo } from 'react';

interface GetPropertiesCountProps {
  qConfigId: string;
  qConfigStructureId: string;
  selectedSubtopics?: string[];
  selectedLOS?: string[];
  locationSelected: boolean;
}

export function useGetPropertiesCount(props: Readonly<GetPropertiesCountProps>) {
  const {
    qConfigId,
    qConfigStructureId,
    selectedSubtopics,
    locationSelected: isLocationSelected,
    selectedLOS,
  } = props;
  const isSelectedSubtopics = selectedSubtopics?.length !== 0;

  const { data: getSingleQuestionPropertiesResult, loading: isGettingQuestionPropertiesCount } =
    useGetQConfigChapterSingleQuestionPropertiesCountQuery({
      variables: {
        input: {
          qConfigId,
          qConfigStructureId,
          location: {
            locationType: isSelectedSubtopics
              ? AddQuestionQConfigStructureLocationType.QbankStructureLevelItem
              : AddQuestionQConfigStructureLocationType.LearningObjectiveStatement,
            qBankStructureLevelItemIds: isSelectedSubtopics ? selectedSubtopics : undefined,
            losIds: !isSelectedSubtopics ? selectedLOS : undefined,
          },
        },
      },
      skip: !isLocationSelected,
      fetchPolicy: 'network-only',
    });

  const singleQuestionProperties = useMemo(
    () => getSingleQuestionPropertiesResult?.excGetQConfigChapterSingleQuestionPropertiesCount,
    [getSingleQuestionPropertiesResult],
  );

  const propertyTagMapper = useMemo(() => {
    if (!singleQuestionProperties) {
      return [];
    }

    const tags = Array.from(new Set(flattenAndMapTags(singleQuestionProperties)));

    return tags;
  }, [singleQuestionProperties]);

  return {
    propertyTagMapper,
    isGettingQuestionPropertiesCount,
    singleQuestionProperties,
  };
}

function flattenAndMapTags(
  result: GetQConfigChapterSingleQuestionPropertiesCountQuery['excGetQConfigChapterSingleQuestionPropertiesCount'],
) {
  return result.flatMap((result) =>
    result.tags.map((tag) => {
      const schemaName = questionPropertyMapper[tag.name as keyof typeof questionPropertyMapper];

      return {
        ...tag,
        schemaName,
      };
    }),
  );
}
